/**
 * Statute Ingestion Page
 *
 * Upload a statute/regulation document and generate draft rules via LLM.
 * Architect-only page for converting regulatory text into FERP configs.
 *
 * Pattern: follows RegistryExplorerPage (stats cards + form + results)
 */

import { useState } from 'react';

import {
  useAnalyzePublicationMutation,
  useExtractStatuteMetadataMutation,
  type AnalyzePublicationMutation,
} from '@/generated/graphql';
import {
  IconFileText,
  IconAlertTriangle,
  IconUpload,
  IconTag,
  IconPercentage,
  IconFileCode,
  IconSparkles,
  IconLink,
} from '@tabler/icons-react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  SimpleGrid,
  Loader,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Button,
  Code,
  Divider,
} from '@mantine/core';
// ============================================================================
// GraphQL Mutations
// ============================================================================

// ============================================================================
// Types
// ============================================================================

type AnalyzePublicationResult = AnalyzePublicationMutation['analyzePublication'];

interface AnalyzePublicationInput {
  input: {
    associationRef: string;
    publicationTitle: string;
    publicationYear: number;
    industry: string;
    jurisdiction: string;
    documentText: string;
  };
}

interface ExtractMetadataResult {
  extractStatuteMetadata: {
    associationRef: string;
    publicationTitle: string;
    publicationYear: number;
    industry: string;
    jurisdiction: string;
    documentText: string;
  };
}

// ============================================================================
// Industry options
// ============================================================================

const INDUSTRY_OPTIONS = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'legal', label: 'Legal' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'government', label: 'Government' },
];

// ============================================================================
// Components
// ============================================================================

function StatsCards({ result }: { result: AnalyzePublicationResult }) {
  return (
    <SimpleGrid cols={{ base: 2, sm: 3 }} mb="lg">
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue"><IconFileCode size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Drafts Generated</Text>
        </Group>
        <Text size="lg" fw={700}>{result.draftCount}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="teal"><IconPercentage size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Confidence</Text>
        </Group>
        <Text size="lg" fw={700}>{(result.confidence * 100).toFixed(1)}%</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="orange"><IconTag size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Tags Mapped</Text>
        </Group>
        <Text size="lg" fw={700}>{result.mappedTags.length}</Text>
      </Card>
    </SimpleGrid>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function StatuteIngestionPage() {
  const [associationRef, setAssociationRef] = useState('');
  const [publicationTitle, setPublicationTitle] = useState('');
  const [publicationYear, setPublicationYear] = useState<number | ''>(new Date().getFullYear());
  const [industry, setIndustry] = useState<string | null>(null);
  const [jurisdiction, setJurisdiction] = useState('');
  const [documentText, setDocumentText] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');

  const [analyzePublication, { data, loading, error }] = useAnalyzePublicationMutation();

  const [extractMetadata, { loading: extracting, error: extractError }] = useExtractStatuteMetadataMutation();

  const handleAutoFill = async () => {
    if (!sourceUrl.trim() && !documentText.trim()) return;
    const result = await extractMetadata({
      variables: {
        input: {
          sourceUrl: sourceUrl.trim() || undefined,
          documentText: documentText.trim() || undefined,
        },
      },
    });
    if (result.data?.extractStatuteMetadata) {
      const m = result.data.extractStatuteMetadata;
      if (m.associationRef) setAssociationRef(m.associationRef);
      if (m.publicationTitle) setPublicationTitle(m.publicationTitle);
      if (m.publicationYear) setPublicationYear(m.publicationYear);
      if (m.industry) setIndustry(m.industry);
      if (m.jurisdiction) setJurisdiction(m.jurisdiction);
      if (m.documentText && !documentText.trim()) setDocumentText(m.documentText);
    }
  };

  const result = data?.analyzePublication;

  const handleSubmit = () => {
    if (!industry || !publicationYear) return;
    analyzePublication({
      variables: {
        input: {
          associationRef,
          publicationTitle,
          publicationYear: Number(publicationYear),
          industry,
          jurisdiction,
          documentText,
        },
      },
    });
  };

  const isFormValid =
    associationRef.trim() !== '' &&
    publicationTitle.trim() !== '' &&
    publicationYear !== '' &&
    industry !== null &&
    jurisdiction.trim() !== '' &&
    documentText.trim() !== '';

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>Statute Ingestion</Title>
          <Text size="sm" c="dimmed">
            Upload regulatory text and generate draft FERP config rules via LLM
          </Text>
        </div>
        <Badge variant="light" color="violet" size="lg">Architect</Badge>
      </Group>

      {error && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Analysis Failed" mb="lg">
          {error.message}
        </Alert>
      )}

      {result && <StatsCards result={result} />}

      {extractError && (
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="Auto-fill Failed" mb="lg">
          {extractError.message}
        </Alert>
      )}

      <Card withBorder p="lg" mb="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>Quick Start — Paste URL or Text</Title>
            <Badge variant="light" color="blue" size="sm">LLM-Assisted</Badge>
          </Group>
          <Text size="xs" c="dimmed">
            Paste a URL to a statute page or paste document text below, then click Auto-Fill to extract metadata.
          </Text>
          <Group align="flex-end">
            <TextInput
              label="Source URL"
              placeholder="https://www.revisor.mn.gov/statutes/cite/144G"
              leftSection={<IconLink size={16} />}
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconSparkles size={16} />}
              variant="light"
              onClick={handleAutoFill}
              loading={extracting}
              disabled={!sourceUrl.trim() && !documentText.trim()}
            >
              Auto-Fill Fields
            </Button>
          </Group>
          <Divider label="or fill manually" labelPosition="center" />
        </Stack>
      </Card>

      <Card withBorder p="lg" mb="lg">
        <Stack gap="md">
          <Title order={4}>Publication Details</Title>

          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Association Reference"
              placeholder="e.g., MN-DHS-2024"
              value={associationRef}
              onChange={(e) => setAssociationRef(e.currentTarget.value)}
              required
            />
            <TextInput
              label="Publication Title"
              placeholder="e.g., Minnesota Statute 144G"
              value={publicationTitle}
              onChange={(e) => setPublicationTitle(e.currentTarget.value)}
              required
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <NumberInput
              label="Publication Year"
              placeholder="2024"
              value={publicationYear}
              onChange={(val) => setPublicationYear(typeof val === 'number' ? val : '')}
              min={1900}
              max={2100}
              required
            />
            <Select
              label="Industry"
              placeholder="Select industry"
              data={INDUSTRY_OPTIONS}
              value={industry}
              onChange={setIndustry}
              required
            />
            <TextInput
              label="Jurisdiction"
              placeholder="e.g., US-MN, US-CA, federal"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.currentTarget.value)}
              required
            />
          </SimpleGrid>

          <Textarea
            label="Document Text"
            placeholder="Paste the full statute or regulation text here..."
            value={documentText}
            onChange={(e) => setDocumentText(e.currentTarget.value)}
            minRows={10}
            maxRows={30}
            autosize
            required
          />

          <Group justify="flex-end">
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={handleSubmit}
              loading={loading}
              disabled={!isFormValid}
            >
              Analyze Publication
            </Button>
          </Group>
        </Stack>
      </Card>

      {result && result.success && (
        <Stack gap="lg">
          {result.warnings.length > 0 && (
            <Stack gap="xs">
              {result.warnings.map((warning, i) => (
                <Alert
                  key={i}
                  icon={<IconAlertTriangle size={16} />}
                  color="yellow"
                  variant="light"
                >
                  {warning}
                </Alert>
              ))}
            </Stack>
          )}

          {result.mappedTags.length > 0 && (
            <Card withBorder p="md">
              <Text size="sm" fw={600} mb="sm">Mapped Tags</Text>
              <Group gap="xs">
                {result.mappedTags.map((tag) => (
                  <Badge key={tag} variant="light" color="blue">{tag}</Badge>
                ))}
              </Group>
            </Card>
          )}

          {result.proposals.length > 0 && (
            <Card withBorder p="md">
              <Text size="sm" fw={600} mb="sm">
                Generated Proposals ({result.proposals.length})
              </Text>
              <Stack gap="sm">
                {result.proposals.map((p) => (
                  <Card key={p.proposalId} withBorder p="xs">
                    <Group gap="xs">
                      <Code fw={700} fz="xs">{p.proposalId}</Code>
                      <Badge variant="light" size="xs">{p.configType}</Badge>
                      {p.safetyLevel && (
                        <Badge variant="light" size="xs" color={
                          p.safetyLevel === 'critical' ? 'red' : p.safetyLevel === 'high' ? 'orange' : 'gray'
                        }>{p.safetyLevel}</Badge>
                      )}
                    </Group>
                    {p.displayName && <Text size="sm" fw={500} mt={4}>{p.displayName}</Text>}
                    {p.description && <Text size="xs" c="dimmed" mt={2} lineClamp={2}>{p.description}</Text>}
                  </Card>
                ))}
              </Stack>
            </Card>
          )}
        </Stack>
      )}
    </Container>
  );
}
